import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getCurrentDate } from "../utils/getCurrentDate";
import { useNavigate } from "react-router-dom";
import {
  useGetOrdersQuery,
  useUpdateOrderStatusMutation,
  useUpdateInvoiceDateMutation,
} from "../slices/OrdersSlice";
import { toast } from "react-toastify";
import ReUsableModal from "../components/ReUsableModal";
import PaymentModal from "../components/PaymentModal";
import InvoiceModal from "../components/InvoiceModal";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { printReceipt } from "../utils/printReceipt";
import { useGetMyOutletFoodItemsQuery } from "../slices/FoodItemSilce";
import {
  FaDownload,
  FaEdit,
  FaFileInvoice,
  FaPrint,
  FaRegEdit,
  FaRupeeSign,
} from "react-icons/fa";
import { cardStyle } from "../components/OutletFormKit";
import jsPDF from "jspdf";
import { formatAmount } from "../utils/commonUtils";
import { canAccess } from "../utils/adminAccess";
import "jspdf-autotable";

const headStyle = {
  backgroundColor: "#f4f9fd",
  color: "#1d5f91",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  borderBottom: "2px solid #e3ecf3",
  padding: "0.6rem 0.6rem",
  whiteSpace: "nowrap",
};

const cellStyle = {
  padding: "0.55rem 0.6rem",
  verticalAlign: "middle",
  fontSize: "0.85rem",
};

const chipBaseStyle = {
  padding: "0.4rem 0.9rem",
  borderRadius: "999px",
  fontSize: "0.83rem",
  fontWeight: "600",
  border: "1px solid #cfe3f2",
  backgroundColor: "#ffffff",
  color: "#1d5f91",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const chipActiveStyle = {
  ...chipBaseStyle,
  border: "1px solid #b7dbf7",
  background:
    "linear-gradient(to bottom right, #ffffff 0%, #d9ebf9 45%, #c2e5fc 100%)",
};

// Orders in these statuses never became a sale, so they are left out of
// the revenue / profit metrics and the Item-wise Sales table
const NON_SALE_STATUSES = [
  "Payment_Pending",
  "Rejected",
  "Not_Placed",
  "Delivery_Rejected",
  "Could_Not_Deliver",
];

// Per-unit making cost saved on an order line when the order was placed,
// so later edits to the item do not change past orders' cost and profit.
// null for orders placed before the backend started saving it
const storedMakingCostOf = (orderLine) => {
  if (orderLine?.makingCost === undefined || orderLine?.makingCost === null) {
    return null;
  }
  const value = Number(orderLine.makingCost);
  return Number.isFinite(value) ? value : null;
};

const statChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.4rem 0.85rem",
  borderRadius: "14px",
  border: "1px solid #b7dbf7",
  background:
    "linear-gradient(to bottom right, #ffffff 0%, #d9ebf9 45%, #c2e5fc 100%)",
  color: "#1d5f91",
  fontWeight: "600",
  fontSize: "0.82rem",
  whiteSpace: "nowrap",
};

const StatChip = ({ label, value, color }) => (
  <span style={{ ...statChipStyle, ...(color ? { color } : {}) }}>
    {label} : ₹ {formatAmount(value)}
  </span>
);

const FilterChip = ({ active, onClick, label, count }) => (
  <button
    type="button"
    style={active ? chipActiveStyle : chipBaseStyle}
    onClick={onClick}
  >
    {label} : <strong>{count}</strong>
  </button>
);

const StatusPill = ({ status }) => {
  const failed = [
    "Rejected",
    "Delivery_Rejected",
    "Could_Not_Deliver",
    "Not_Placed",
  ].includes(status);
  const delivered = status === "Delivered";
  const colors = delivered
    ? { bg: "#e8f6ee", border: "#b7e0c6", color: "#1e7e34" }
    : failed
    ? { bg: "#fdecec", border: "#f5c2c7", color: "#c0392b" }
    : { bg: "#eef6fc", border: "#d6eaf8", color: "#1d5f91" };
  return (
    <span
      style={{
        padding: "0.15rem 0.6rem",
        borderRadius: "999px",
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.color,
        fontWeight: "600",
        fontSize: "0.75rem",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
};

const OrdersHistoryScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const { newOrders } = useSelector((state) => state.newOrders);
  const [orderId, setOrderId] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [deliveryOtp, setDeliveryOtp] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [orderQuantity, setOrderQuantity] = useState([]);
  const [displayOrderId, setDisplayOrderId] = useState("");
  const [apiQuery, setApiQuery] = useState(
    `orderDate[gte]=${getCurrentDate()}&sort=-orderDate`
  );
  const [showDelivered, setShowDelivered] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const navigate = useNavigate();
  const [showOutletOrders, setShowOutletOrders] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [hasClickedFilter, setHasClickedFilter] = useState(false);
  const [payingOrder, setPayingOrder] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  // Order whose invoice date is being edited + the picked date
  const [invoiceDateOrder, setInvoiceDateOrder] = useState(null);
  const [newInvoiceDate, setNewInvoiceDate] = useState("");

  const {
    data: orders,
    isLoading,
    error,
    refetch: orderRefetch,
  } = useGetOrdersQuery({ query: apiQuery, token: userInfo?.token });

  const {
    data: foods,
    isLoading: foodsLoading,
    refetch: foodsRefetch,
    error: foodsError,
  } = useGetMyOutletFoodItemsQuery({ token: userInfo.token });

  const [ordersToList, setOrdersToList] = useState(orders);

  const [updateStatus, { isLoading: updateStatusLoading }] =
    useUpdateOrderStatusMutation();

  const [updateInvoiceDate] = useUpdateInvoiceDateMutation();

  const openInvoiceDateEditor = (order) => {
    setInvoiceDateOrder(order);
    setNewInvoiceDate(
      new Date(order.orderDate || order.createdAt).toISOString().slice(0, 10)
    );
  };

  const handleInvoiceDateSubmit = async () => {
    try {
      await updateInvoiceDate({
        order_id: invoiceDateOrder?._id,
        invoiceDate: newInvoiceDate,
        token: userInfo?.token,
      }).unwrap();
      toast.success("Invoice date updated");
      setInvoiceDateOrder(null);
      orderRefetch();
    } catch (e) {
      toast.error(e?.data?.error || "Failed to update invoice date");
    }
  };

  useEffect(() => {
    if (!ordersToList) return;
    if (!hasClickedFilter && !searchText.trim()) {
      setFilteredOrders(ordersToList);
      return;
    }

    const term = searchText.trim().toLowerCase();

    let updatedOrders = [];

    if (term) {
      if (term.length < 4) {
        // Match only orderId
        updatedOrders = ordersToList.filter((order) => {
          const orderIdStr = String(order?.orderId || "").toLowerCase();
          return orderIdStr.includes(term);
        });
      } else {
        // Match only mobileNumber
        updatedOrders = ordersToList.filter((order) => {
          const mobileNumberStr = String(
            order?.mobileNumber || ""
          ).toLowerCase();
          return mobileNumberStr.includes(term);
        });
      }
    } else {
      // If no search term, use filters
      if (showOutletOrders) {
        updatedOrders = ordersToList.filter(
          (order) => order.orderType === "outlet"
        );
      } else if (showDelivered) {
        updatedOrders = ordersToList.filter(
          (order) =>
            order.orderStatus === "Delivered" && order.orderType === "online"
        );
      } else {
        updatedOrders = ordersToList.filter(
          (order) =>
            order.orderStatus !== "Delivered" && order.orderType === "online"
        );
      }
    }

    setFilteredOrders(updatedOrders);
  }, [
    searchText,
    ordersToList,
    showDelivered,
    showOutletOrders,
    hasClickedFilter,
  ]);

  const handleOrderStatusUpdate = async () => {
    try {
      const data = {
        _id: orderId,
        orderStatus: newStatus,
        deliveryOtp: deliveryOtp,
        token: userInfo.token,
      };
      await updateStatus(data).unwrap();
      toast.success("Status Updated Successfully");
      orderRefetch();
    } catch (e) {
      toast.error(e?.data?.error);
    }
  };

  const handleShowDelivered = () => {
    if (!ordersToList) return;
    const deliveredToday = ordersToList?.filter(
      (order) => order?.orderStatus === "Delivered"
    );
    setFilteredOrders(deliveredToday);
    setShowDelivered(true);
    setShowOutletOrders(false);
    setHasClickedFilter(true);
  };

  useEffect(() => {
    let orderDetails = {};
    const ors = isLoading ? [] : orders ? orders : [];
    setOrdersToList(ors);
    ors?.map((allOrs) => {
      if (allOrs.orderStatus !== "Delivered") {
        allOrs?.orderDetails?.map((or) => {
          if (orderDetails[or.foodItemId + "_" + or.itemName]) {
            orderDetails[or.foodItemId + "_" + or.itemName] += or.itemQuantity;
          } else {
            orderDetails[or.foodItemId + "_" + or.itemName] = or.itemQuantity;
          }
        });
      }
    });
    setOrderQuantity(orderDetails);
  }, [orders]);

  useEffect(() => {
    const ors = isLoading ? [] : orders || [];
    setOrdersToList(ors);
    if (!hasClickedFilter) {
      setFilteredOrders(ors); // show all orders on load
    }
  }, [orders]);

  const handleShowAllOrders = () => {
    if (!orders) return;
    const nonDelivered = orders.filter(
      (order) =>
        order.orderStatus !== "Delivered" && order.orderType === "online"
    );
    setFilteredOrders(nonDelivered);
    setShowDelivered(false);
    setShowOutletOrders(false);
    setHasClickedFilter(true);
  };

  const handleShowOutletOrders = () => {
    setShowDelivered(false);
    setShowOutletOrders(true);
    setHasClickedFilter(true);
  };

  const getOrderHandler = () => {
    if (endDate < startDate) {
      toast.error("End date cannot be earlier than Start date");
    } else {
      const start = `${startDate}T00:00:00.000Z`;
      const end = `${endDate}T23:59:59.999Z`;
      setApiQuery(
        `orderDate[gte]=${start}&orderDate[lte]=${end}&sort=-orderDate`
      );
      setHasClickedFilter(false);
    }
  };

  const printBill = async (orderDetails) => {
    await printReceipt(orderDetails, userInfo, "manual");
  };

  const downloadOrdersPDF = (ordersList) => {
    if (!ordersList || ordersList.length === 0) {
      toast.error("No orders to download");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;

    const firstOrder = ordersList[0] || {};
    const outletSource = firstOrder.outletId || firstOrder;

    const outletName = outletSource.outletName || "Outlet";
    const outletAddress = outletSource.outletAddress || "";
    const outletContact = outletSource.outletContact || "";
    const outletPincode = outletSource.outletPincode || "";
    let totalSales = 0;
    let totalGst = 0;
    let totalMakingCost = 0;
    const allFoods = userInfo?.AllFoods ?? userInfo?.allFoods ?? [];

    let y = 20;
    doc.setFontSize(16);
    doc.text(outletName, centerX, y, { align: "center" });
    y += 7;

    doc.setFontSize(10);
    if (outletAddress) {
      // The address is often saved over several lines (and long lines wrap),
      // so move down by the height of every drawn line; otherwise the
      // contact line below is printed on top of the address
      const addressText = outletPincode
        ? `${outletAddress} - ${outletPincode}`
        : outletAddress;
      const addressLines = doc.splitTextToSize(addressText, pageWidth - 60);
      const lineHeight =
        (doc.getFontSize() * doc.getLineHeightFactor()) /
        doc.internal.scaleFactor;
      doc.text(addressLines, centerX, y, { align: "center" });
      y += addressLines.length * lineHeight + 2;
    }

    if (outletContact) {
      doc.text(`Contact: ${outletContact}`, centerX, y, {
        align: "center",
      });
      y += 6;
    }

    doc.line(30, y + 2, pageWidth - 30, y + 2);
    y += 10;
    const tableColumn = [
      "Order ID",
      "Date",
      "Status",
      "Items",
      "Making Cost",
      "Amount",
    ];

    const tableRows = [];

    ordersList.forEach((order) => {
      const items = (order.orderDetails || [])
        .map((i) => {
          const size =
            i.sizeName && i.sizeName.toLowerCase() !== "single size"
              ? ` - ${i.sizeName}`
              : "";
          return `${i.itemName}${size}  (${i.itemQuantity})`;
        })
        .join(", ");

      let orderAmountSum = 0;
      let orderMakingTotal = 0;

      (order.orderDetails || []).forEach((i) => {
        const qty = Number(i.itemQuantity || 0);

        const unitSelling = Number(i.sellingPriceAfterOffers || 0);
        const unitGst = Number(i.gstAmount || 0);

        const baseName = String(i.itemName || "")
          .split("-")[0]
          .trim();
        const norm = (s) =>
          String(s || "")
            .trim()
            .toLowerCase();

        let foodDef =
          (allFoods || []).find((f) => norm(f.itemName) === norm(baseName)) ||
          (allFoods || []).find((f) =>
            norm(f.itemName).startsWith(norm(baseName))
          ) ||
          (allFoods || []).find((f) =>
            norm(f.itemName).includes(norm(baseName))
          );

        if (!foodDef && (i.foodItemId || i.itemId)) {
          foodDef =
            (allFoods || []).find(
              (f) =>
                String(f._id) === String(i.foodItemId) ||
                String(f._id) === String(i.itemId) ||
                String(f.itemCode) === String(i.itemCode)
            ) || foodDef;
        }

        let unitMaking = 0;

        if (foodDef) {
          const sizes = Array.isArray(foodDef.availableSizes)
            ? foodDef.availableSizes
            : [];

          if (sizes.length) {
            const matchSize = sizes.find(
              (s) =>
                norm(s.sizeName) === norm(i.sizeName) ||
                (!i.sizeName && norm(s.sizeName) === "single size")
            );
            const sizeToUse =
              matchSize || (sizes.length === 1 ? sizes[0] : null);
            if (sizeToUse) {
              unitMaking =
                Number(
                  sizeToUse.makingCost ??
                    sizeToUse.making_cost ??
                    sizeToUse.makingCostPerUnit ??
                    sizeToUse.making_cost_per_unit ??
                    0
                ) || 0;
            } else {
              unitMaking = 0;
            }
          } else {
            unitMaking =
              Number(foodDef.makingCost ?? foodDef.making_cost ?? 0) || 0;
          }
        }
        const storedMakingCost = storedMakingCostOf(i);
        if (storedMakingCost !== null) unitMaking = storedMakingCost;

        const itemAmount = (unitSelling + unitGst) * qty;
        const itemGst = unitGst * qty;
        const itemMaking = unitMaking * qty;

        orderAmountSum += itemAmount;
        totalSales += itemAmount;
        totalGst += itemGst;
        orderMakingTotal += itemMaking;
        totalMakingCost += itemMaking;
      });

      const amountToShow =
        !isNaN(orderAmountSum) && orderAmountSum !== 0
          ? orderAmountSum
          : Number(order.grossTotal || 0);

      tableRows.push([
        order.orderId,
        new Date(order.orderDate || order.createdAt).toISOString().slice(0, 10),
        order.orderStatus,
        items,
        orderMakingTotal.toFixed(2),
        String(Number(amountToShow).toFixed(2)),
      ]);
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: y,
      styles: { fontSize: 9, halign: "center" },
      headStyles: { fillColor: [0, 0, 0], halign: "center" },
      columnStyles: {
        5: { halign: "center" },
      },
    });

    const finalY =
      (doc.lastAutoTable && doc.lastAutoTable.finalY) ||
      doc.autoTableEndPosY() ||
      y + 10;
    const spacing = 8;
    let totalsY = finalY + spacing;

    doc.setFontSize(11);
    doc.text(
      `Total Sales Amount: ${totalSales.toFixed(2)}`,
      pageWidth - 40,
      totalsY,
      { align: "right" }
    );
    totalsY += 7;
    doc.text(`Total GST: ${totalGst.toFixed(2)}`, pageWidth - 40, totalsY, {
      align: "right",
    });
    totalsY += 7;
    doc.text(
      `Total Making Cost: ${totalMakingCost.toFixed(2)}`,
      pageWidth - 40,
      totalsY,
      { align: "right" }
    );

    const pageHeight = doc.internal.pageSize.height;
    const now = new Date();
    const footerText =
      "Downloaded on: " +
      now.toLocaleDateString() +
      " " +
      now.toLocaleTimeString();

    doc.setFontSize(10);
    doc.text(footerText, 10, pageHeight - 10);

    doc.save("Orders.pdf");
  };

  // Top metrics and the Item-wise Sales table, computed together so they
  // always agree. They cover every order fetched for the selected date
  // range (not just the chip/search view), excluding orders that never
  // became a sale. Per order line:
  //   revenue  = (price after offers + GST) x qty   (platform fee excluded)
  //   discount = (itemPrice x discount% + flat discount) x qty
  //   making   = making cost saved on the line at order time; older orders
  //              fall back to the item's current making cost
  //   profit   = revenue - GST - making cost
  const { periodTotals, salesMetrics } = useMemo(() => {
    const totals = {
      revenue: 0,
      gst: 0,
      discount: 0,
      makingCost: 0,
      profit: 0,
    };
    const itemMap = {};
    const norm = (s) =>
      String(s || "")
        .trim()
        .toLowerCase();
    const foodDefs = Array.isArray(foods)
      ? foods
      : Object.values(userInfo?.AllFoods || {});

    (ordersToList || [])
      .filter((order) => !NON_SALE_STATUSES.includes(order.orderStatus))
      .forEach((order) => {
        (order.orderDetails || []).forEach((item) => {
          const qty = Number(item.itemQuantity) || 0;
          const ratePerUnit = Number(item.sellingPriceAfterOffers) || 0;
          const gstPerUnit = Number(item.gstAmount) || 0;

          const listPrice = Number(item.sellingPrice) || 0;
          const discountPercent =
            Number(item.discountPercent) > 0 ? Number(item.discountPercent) : 0;
          const discountAmount =
            Number(item.discountAmount) > 0 ? Number(item.discountAmount) : 0;
          const discountPerUnit = Math.min(
            listPrice,
            (listPrice * discountPercent) / 100 + discountAmount
          );

          const foodItemId = String(
            item.foodItemId?._id || item.foodItemId || ""
          );
          const foodDef =
            foodDefs.find((f) => foodItemId && String(f._id) === foodItemId) ||
            foodDefs.find((f) => norm(f.itemName) === norm(item.itemName));
          const sizes = Array.isArray(foodDef?.availableSizes)
            ? foodDef.availableSizes
            : [];
          const size =
            sizes.find((s) => norm(s.sizeName) === norm(item.sizeName)) ||
            (sizes.length === 1 ? sizes[0] : null);

          const storedMakingCost = storedMakingCostOf(item);
          const makingPerUnit =
            storedMakingCost !== null
              ? storedMakingCost
              : Number(size?.makingCost) || 0;

          // Stock units used per unit sold, as saved on the order when
          // available; otherwise the item's current value (0 counts as 1)
          const consumptionPerUnit =
            Number(item.consumptionQty) > 0
              ? Number(item.consumptionQty)
              : Number(size?.consumptionQty) > 0
              ? Number(size.consumptionQty)
              : 1;

          const lineRevenue = (ratePerUnit + gstPerUnit) * qty;
          const lineGst = gstPerUnit * qty;
          const lineMaking = makingPerUnit * qty;

          totals.revenue += lineRevenue;
          totals.gst += lineGst;
          totals.discount += discountPerUnit * qty;
          totals.makingCost += lineMaking;

          const key = foodItemId || norm(item.itemName);
          if (!itemMap[key]) {
            itemMap[key] = {
              key,
              itemName: item.itemName,
              totalQuantity: 0,
              totalAmount: 0,
              totalGst: 0,
              totalMakingCost: 0,
              totalProfit: 0,
            };
          }
          itemMap[key].totalQuantity += qty * consumptionPerUnit;
          itemMap[key].totalAmount += lineRevenue;
          itemMap[key].totalGst += lineGst;
          itemMap[key].totalMakingCost += lineMaking;
          itemMap[key].totalProfit += lineRevenue - lineGst - lineMaking;
        });
      });

    totals.profit = totals.revenue - totals.gst - totals.makingCost;
    return {
      periodTotals: totals,
      salesMetrics: Object.values(itemMap).sort(
        (a, b) => b.totalAmount - a.totalAmount
      ),
    };
  }, [ordersToList, foods, userInfo?.AllFoods]);

  const openOrdersCount = ordersToList?.filter(
    (order) => order.orderStatus !== "Delivered" && order.orderType === "online"
  )?.length;
  const deliveredOrdersCount = ordersToList?.filter(
    (order) => order.orderStatus === "Delivered"
  )?.length;
  const outletOrdersCount = ordersToList?.filter(
    (order) => order.orderType === "outlet"
  )?.length;

  const listTitle = searchText.trim()
    ? "Searched Orders"
    : !hasClickedFilter
    ? "All Orders"
    : showDelivered
    ? "Delivered Orders"
    : showOutletOrders
    ? "Outlet Orders"
    : "Open Orders";

  return (
    <div style={{ padding: "0.5rem 0.6rem" }}>
      <ReUsableModal
        title="Update Order Status"
        payload={[
          {
            fieldName: "Order ID",
            fieldType: "text",
            fieldValue: displayOrderId,
            setMethod: setDisplayOrderId,
            disabled: true,
          },
          {
            fieldName: "Status",
            fieldType: "select",
            fieldValue: newStatus,
            setMethod: setNewStatus,
            options: [
              "Placed",
              "Confirmed",
              "Preparing",
              "Ready",
              "In_Delivery",
              "Delivered",
              "Delivery_Rejected",
              "Could_Not_Deliver",
              "Not_Placed",
              "Rejected",
            ],
          },
          ...(newStatus === "Delivered"
            ? [
                {
                  fieldName: "Delivery OTP",
                  fieldType: "text",
                  fieldValue: deliveryOtp,
                  setMethod: setDeliveryOtp,
                },
              ]
            : []),
        ]}
        show={showModal}
        submitDisabled={
          newStatus === "Delivered" && !/^\d{4}$/.test(deliveryOtp)
        }
        size="md"
        onHide={() => setShowModal(false)}
        onSubmit={handleOrderStatusUpdate}
        submit="Update"
        cancel="Cancel"
      />

      {payingOrder && (
        <PaymentModal
          show={Boolean(payingOrder)}
          onHide={() => setPayingOrder(null)}
          order={payingOrder}
          onSuccess={() => orderRefetch()}
        />
      )}

      {invoiceOrder && (
        <InvoiceModal
          show={Boolean(invoiceOrder)}
          onHide={() => setInvoiceOrder(null)}
          order={invoiceOrder}
        />
      )}

      {invoiceDateOrder && (
        <ReUsableModal
          title={`Update Invoice Date - Order ${invoiceDateOrder.orderId}`}
          payload={[
            {
              fieldName: "Invoice Date",
              fieldType: "date",
              fieldValue: newInvoiceDate,
              setMethod: setNewInvoiceDate,
              isMandatory: true,
            },
          ]}
          show={Boolean(invoiceDateOrder)}
          size="md"
          submitDisabled={!newInvoiceDate}
          onHide={() => setInvoiceDateOrder(null)}
          onSubmit={handleInvoiceDateSubmit}
          submit="Submit"
          cancel="Cancel"
        />
      )}

      {/* Filter card */}
      <div
        style={{ ...cardStyle, padding: "0.9rem 1rem", marginBottom: "0.8rem" }}
      >
        <Row className="align-items-end" style={{ rowGap: "0.6rem" }}>
          <Col xs={12} md={3}>
            <h5 className="mb-1">
              <strong style={{ color: "#1d5f91" }}>Order History</strong>
            </h5>
            <small className="text-muted">
              Select a date range and click GET
            </small>
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              FROM
            </Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Label
              className="mb-1"
              style={{ fontSize: "0.75rem", color: "#6c757d" }}
            >
              TO
            </Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} md={2} className="d-grid">
            <Button
              className={`${userInfo?.themeColor}ThemeHeaderTop`}
              style={{ borderRadius: "8px", fontWeight: "600" }}
              onClick={getOrderHandler}
            >
              GET
            </Button>
          </Col>
          <Col xs={6} sm={3} md={2} className="d-grid">
            <Button
              variant="outline-primary"
              style={{ borderRadius: "8px", fontWeight: "600" }}
              onClick={() => downloadOrdersPDF(filteredOrders)}
            >
              <FaDownload style={{ marginBottom: "2px" }} /> PDF
            </Button>
          </Col>
        </Row>
      </div>

      {/* Filter chips + search */}
      <div
        className="d-flex flex-wrap align-items-center"
        style={{ gap: "0.6rem", marginBottom: "0.8rem" }}
      >
        <FilterChip
          active={hasClickedFilter && !showDelivered && !showOutletOrders}
          onClick={handleShowAllOrders}
          label="Open Orders"
          count={openOrdersCount}
        />
        <FilterChip
          active={showDelivered}
          onClick={handleShowDelivered}
          label="Delivered"
          count={deliveredOrdersCount}
        />
        <FilterChip
          active={showOutletOrders}
          onClick={handleShowOutletOrders}
          label="Outlet Orders"
          count={outletOrdersCount}
        />
        <Form.Control
          type="text"
          placeholder="Order ID or Mobile Number"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value.replace(/\D/g, ""))}
          style={{ maxWidth: "16rem", flex: "1 1 12rem" }}
        />
        {new Date(newOrders[0]?.createdAt).getTime() + 180 * 1000 >
          new Date().getTime() &&
          newOrders[0].orderType !== "outlet" && (
            <Button
              style={{
                backgroundColor: "black",
                fontSize: "0.7rem",
                border: "0.2px solid",
              }}
              className="numbersButton secondaryButton"
            >
              {newOrders?.map((ors, index) => (
                <div
                  key={index}
                  className="twinkle-text"
                  style={{ color: "orange" }}
                >
                  {ors.orderDetails?.map((item, idx) => (
                    <span key={idx}>
                      {item.itemName} - {item.itemQuantity}{" "}
                    </span>
                  ))}
                </div>
              ))}
            </Button>
          )}
      </div>

      {/* Period totals for the listed orders */}
      <div
        className="d-flex flex-wrap align-items-center"
        style={{ gap: "0.6rem", marginBottom: "0.8rem" }}
      >
        <StatChip label="Revenue" value={periodTotals.revenue} />
        <StatChip label="GST" value={periodTotals.gst} />
        <StatChip label="Discount" value={periodTotals.discount} />
        <StatChip label="Making Cost" value={periodTotals.makingCost} />
        <StatChip
          label="Profit"
          value={periodTotals.profit}
          color={periodTotals.profit >= 0 ? "#1e8e4e" : "#c0392b"}
        />
      </div>

      <Row style={{ rowGap: "0.8rem" }}>
        {/* Orders list */}
        <Col xs={12} lg={8}>
          {isLoading ? (
            <Loader />
          ) : error ? (
            <Message>{error?.data?.error}</Message>
          ) : orders ? (
            <div style={{ ...cardStyle, overflow: "hidden" }}>
              <div
                className="d-flex justify-content-between align-items-center flex-wrap"
                style={{
                  padding: "0.7rem 1rem",
                  borderBottom: "1px solid #eef4f9",
                  gap: "0.5rem",
                }}
              >
                <strong style={{ color: "#1d5f91", fontSize: "0.92rem" }}>
                  {listTitle}
                </strong>
                <small className="text-muted">
                  {filteredOrders?.length || 0} orders
                </small>
              </div>
              <div style={{ maxHeight: "68vh", overflow: "auto" }}>
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={headStyle}>Order</th>
                      <th style={headStyle}>Items</th>
                      <th style={headStyle}>Count</th>
                      <th style={headStyle}>Date</th>
                      <th style={headStyle}>Status</th>
                      <th style={headStyle}>OTP</th>
                      <th style={headStyle}>Amount</th>
                      <th style={headStyle}>Pending</th>
                      <th style={headStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders?.map((ors) => (
                      <tr key={ors._id}>
                        <td style={cellStyle}>
                          <a
                            style={{
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate("/orderDetails", {
                                state: { orderResponse: ors },
                              })
                            }
                          >
                            <span
                              style={{
                                color: "#1d5f91",
                                fontWeight: "600",
                                textDecoration: "underline",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {ors.orderId}
                            </span>
                            {ors.invoiceNumber && (
                              <>
                                <br />
                                <small className="text-muted">
                                  Inv: {ors.invoiceNumber?.slice(10)}
                                </small>
                              </>
                            )}
                          </a>
                        </td>
                        <td style={{ ...cellStyle, minWidth: "11rem" }}>
                          {ors.orderDetails?.map((item, idx) => (
                            <div key={idx} style={{ fontSize: "0.82rem" }}>
                              {item.itemName}{" "}
                              {item?.sizeName !== "Single Size" && (
                                <small style={{ color: "#1e7e34" }}>
                                  {item?.sizeName}{" "}
                                </small>
                              )}
                              <span className="text-muted">
                                × {item.itemQuantity}
                              </span>
                            </div>
                          ))}
                        </td>
                        <td style={cellStyle}>
                          {ors.orderDetails.reduce(
                            (total, item) => total + Number(item.itemQuantity),
                            0
                          )}
                        </td>
                        <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                          {new Date(ors?.orderDate || ors?.createdAt)
                            .toISOString()
                            .slice(0, 10)}{" "}
                          {canAccess(userInfo, "edit_invoice_date") && (
                            <FaRegEdit
                              title="Edit invoice date"
                              style={{
                                cursor: "pointer",
                                color: "#1d5f91",
                                marginBottom: "2px",
                              }}
                              onClick={() => openInvoiceDateEditor(ors)}
                            />
                          )}
                          <br />
                          <small className="text-muted">
                            {new Date(
                              new Date(ors?.createdAt).getTime() +
                                5.5 * 60 * 60 * 1000
                            )
                              .toISOString()
                              .slice(11, 16)}
                          </small>
                        </td>
                        <td style={cellStyle}>
                          <StatusPill status={ors.orderStatus} />
                        </td>
                        <td style={cellStyle}>{ors.deliveryOtp || "-"}</td>
                        <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                          <strong>₹ {formatAmount(ors.grossTotal)}</strong>
                        </td>
                        <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                          {ors.dueAmount > 0 ? (
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: "0.45rem" }}
                            >
                              {" "}
                              <strong style={{ color: "#dc3545" }}>
                                ₹ {formatAmount(ors.dueAmount)}
                              </strong>
                              <span
                                size="sm"
                                style={{
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                  color: "green",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                                onClick={() => setPayingOrder(ors)}
                              >
                                Pay Now
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "#1e7e34" }}>
                              {formatAmount(ors.dueAmount)}
                            </span>
                          )}
                        </td>
                        <td style={cellStyle}>
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: "0.4rem" }}
                          >
                            <Button
                              variant="outline-success"
                              size="sm"
                              style={{
                                fontSize: "0.72rem",
                                fontWeight: "600",
                                borderRadius: "6px",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => printBill(ors)}
                            >
                              <FaPrint style={{ marginBottom: "2px" }} /> Print
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              style={{
                                fontSize: "0.72rem",
                                fontWeight: "600",
                                borderRadius: "6px",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => setInvoiceOrder(ors)}
                            >
                              <FaFileInvoice style={{ marginBottom: "2px" }} />{" "}
                              Invoice
                            </Button>
                            {!["Rejected", "Not_Placed"].includes(
                              ors.orderStatus
                            ) && (
                              <Button
                                variant="outline-warning"
                                size="sm"
                                style={{
                                  fontSize: "0.72rem",
                                  fontWeight: "600",
                                  borderRadius: "6px",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() =>
                                  navigate("/sales", {
                                    state: { editOrder: ors },
                                  })
                                }
                              >
                                <FaEdit style={{ marginBottom: "2px" }} /> Edit
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredOrders?.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          style={{
                            ...cellStyle,
                            textAlign: "center",
                            color: "#6c757d",
                            padding: "1.5rem",
                          }}
                        >
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <></>
          )}
        </Col>

        {/* Item-wise sales */}
        <Col xs={12} lg={4}>
          <div style={{ ...cardStyle, overflow: "hidden" }}>
            <div
              style={{
                padding: "0.7rem 1rem",
                borderBottom: "1px solid #eef4f9",
              }}
            >
              <strong style={{ color: "#1d5f91", fontSize: "0.92rem" }}>
                Item-wise Sales
              </strong>
            </div>
            <div style={{ maxHeight: "68vh", overflow: "auto" }}>
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={headStyle}>Food</th>
                    <th style={headStyle}>Qty</th>
                    <th style={headStyle}>Sale ₹</th>
                    <th style={headStyle}>GST ₹</th>
                    <th style={headStyle}>Profit ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {salesMetrics?.length > 0 ? (
                    salesMetrics.map((item) => (
                      <tr key={item.key}>
                        <td style={{ ...cellStyle, fontSize: "0.8rem" }}>
                          {item.itemName}
                        </td>
                        <td style={{ ...cellStyle, fontSize: "0.8rem" }}>
                          {Number(item.totalQuantity.toFixed(2))}
                        </td>
                        <td style={{ ...cellStyle, fontSize: "0.8rem" }}>
                          {formatAmount(item.totalAmount)}
                        </td>
                        <td style={{ ...cellStyle, fontSize: "0.8rem" }}>
                          {formatAmount(item.totalGst)}
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            fontSize: "0.8rem",
                            color: item.totalProfit < 0 ? "#c0392b" : undefined,
                          }}
                        >
                          {formatAmount(item.totalProfit)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          ...cellStyle,
                          textAlign: "center",
                          color: "#6c757d",
                          padding: "1.5rem",
                        }}
                      >
                        No sales data
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default OrdersHistoryScreen;
